import axios from 'axios';
import config from '../config';

// Token state management
let currentAccessToken = config.strava.accessToken;
let tokenRefreshPromise = null;

/**
 * Refresh the Strava access token using the refresh token
 * @returns {Promise<string>} New access token
 */
const refreshAccessToken = async () => {
  // If already refreshing, wait for that to complete
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  tokenRefreshPromise = (async () => {
    try {
      console.log('Refreshing Strava access token...');

      const response = await axios.post('https://www.strava.com/oauth/token', {
        client_id: config.strava.clientId,
        client_secret: config.strava.clientSecret,
        refresh_token: config.strava.refreshToken,
        grant_type: 'refresh_token'
      });

      const { access_token, refresh_token, expires_at } = response.data;

      // Update current token
      currentAccessToken = access_token;

      // Store the new tokens in localStorage for persistence
      localStorage.setItem('strava_access_token', access_token);
      localStorage.setItem('strava_refresh_token', refresh_token);
      localStorage.setItem('strava_token_expires_at', expires_at.toString());

      console.log('Strava access token refreshed successfully');
      return access_token;

    } catch (error) {
      console.error('Failed to refresh Strava token:', error.message);
      throw new Error('TOKEN_REFRESH_FAILED');
    } finally {
      tokenRefreshPromise = null;
    }
  })();

  return tokenRefreshPromise;
};

/**
 * Get current access token, refreshing if needed
 * @returns {Promise<string>} Valid access token
 */
const getValidAccessToken = async () => {
  // Check for stored token first
  const storedToken = localStorage.getItem('strava_access_token');
  const expiresAt = localStorage.getItem('strava_token_expires_at');

  if (storedToken && expiresAt) {
    const expiresAtMs = parseInt(expiresAt, 10) * 1000;
    const bufferMs = 5 * 60 * 1000; // 5 minute buffer

    if (Date.now() < expiresAtMs - bufferMs) {
      currentAccessToken = storedToken;
      return storedToken;
    }
  }

  // Token expired or not found, use config token
  return currentAccessToken || config.strava.accessToken;
};

/**
 * Fetch activity stream (GPS coordinates) from Strava
 * @param {string} activityId - Strava activity ID
 * @returns {Promise<Array>} Array of {lat, lng} coordinates
 */
export const fetchStravaRoute = async (activityId) => {
  try {
    // Check cache first
    const cached = getFromCache(activityId);
    if (cached) {
      console.log(`Using cached data for activity ${activityId}`);
      return cached;
    }

    const accessToken = await getValidAccessToken();

    // Fetch from Strava API
    const url = `https://www.strava.com/api/v3/activities/${activityId}/streams`;
    const response = await axios.get(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      params: {
        keys: 'latlng',
        key_by_type: true
      }
    });

    // Extract coordinates
    const latlngData = response.data.latlng?.data || [];
    const coordinates = latlngData.map(coord => ({
      lat: coord[0],
      lng: coord[1]
    }));

    // Cache the result
    saveToCache(activityId, coordinates);

    console.log(`Fetched ${coordinates.length} coordinates for activity ${activityId}`);
    return coordinates;

  } catch (error) {
    console.error(`Error fetching route for activity ${activityId}:`, error.message);

    // If token expired, refresh and retry once
    if (error.response?.status === 401) {
      console.log('Access token expired, attempting refresh...');
      try {
        await refreshAccessToken();
        // Retry the request with new token
        return fetchStravaRoute(activityId);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError.message);
        // Return empty array to allow app to continue
        return [];
      }
    }

    // Return empty array for graceful degradation
    return [];
  }
};

/**
 * Fetch multiple routes with rate limiting
 * @param {Array<string>} activityIds - Array of activity IDs
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Object>} Object with activityId as key and coordinates as value
 */
export const fetchMultipleRoutes = async (activityIds, onProgress = null) => {
  const routes = {};
  const total = activityIds.length;
  
  // Strava API rate limit: 100 requests per 15 minutes, 1000 per day
  // We'll add a small delay between requests to be safe
  for (let i = 0; i < activityIds.length; i++) {
    const activityId = activityIds[i];
    
    try {
      routes[activityId] = await fetchStravaRoute(activityId);
      
      if (onProgress) {
        onProgress({
          current: i + 1,
          total: total,
          percentage: Math.round(((i + 1) / total) * 100)
        });
      }
      
      // Add delay to respect rate limits (600ms = ~100 requests per minute)
      if (i < activityIds.length - 1) {
        await delay(600);
      }
      
    } catch (error) {
      console.error(`Failed to fetch route for activity ${activityId}`);
      routes[activityId] = [];
    }
  }
  
  return routes;
};

/**
 * Helper function to add delay
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Cache management functions
 */
const CACHE_KEY_PREFIX = 'race_map_route_';

const getFromCache = (activityId) => {
  if (!config.cache.enabled) return null;
  
  try {
    const cached = localStorage.getItem(CACHE_KEY_PREFIX + activityId);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;
    const maxAge = config.cache.expiryDays * 24 * 60 * 60 * 1000;
    
    if (age > maxAge) {
      localStorage.removeItem(CACHE_KEY_PREFIX + activityId);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error reading from cache:', error);
    return null;
  }
};

const saveToCache = (activityId, data) => {
  if (!config.cache.enabled) return;
  
  try {
    const cacheData = {
      data: data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY_PREFIX + activityId, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error saving to cache:', error);
  }
};

/**
 * Clear all cached route data
 */
export const clearRouteCache = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log('Route cache cleared');
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};
